import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { entries, filter, intersection, keys, map, uniq, uniqBy } from "lodash";

import { ArrowLeft } from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  ListItemButton,
  ListItemText,
  ListSubheader,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";

import { IUnitBasic } from "~/routes/api/types";

import { useAppConfigQuery } from "#/features/User/queries";
import { IAppConfig } from "#/features/User/types";
import { IUnit } from "@/api";
import { useFacilityById } from "@/common/AppConfigUtils";
import { useKeyBy } from "@/common/hooks";
import { darkPurple } from "@/common/theming";

import { Checkbox, MenuItem } from "../TrackedComponents";

import { useHandleQueryParams } from "./hooksDeprecated";
import "./UnitPickerDeprecated.scss";

// Constants
const emptyUnitArray: IUnitBasic[] = [];
const NO_GROUP = "__NO_GROUP__"; // Fallback group name when no group is specified
const ALL_SELECTED = ["all"]; // Special value to indicate all units are selected

// Define chip styles
const chipStyles = {
  backgroundColor: "#f5f0ff",
  color: darkPurple,
  borderRadius: 1,
  "& .MuiChip-label": {
    padding: "0 8px",
    fontSize: "0.85rem",
  },
};

// Type definition for unit groups
type TGroups = { [GroupName in string]: { available: IUnit["id"][]; selected: IUnit["id"][] } };

/**
 * UnitPicker component - Allows selecting units with grouping capabilities
 * Units can be grouped by their unitGroupName configuration or by facility
 */
export const UnitPickerDeprecated = memo(
  ({
    onChange,
    values,
    valueIds: valueIdsFromParams,
    facilityId: _facilityId,
    unitOptions: availableUnitsFromParams,
    withQueryParams = false,
    showFullUnitNames = false,
    size,
    className,
  }: {
    onChange: (selectedUnits: IUnit[]) => void; // Callback when selection changes
    values?: IUnit[]; // Selected units (alternative to valueIds)
    valueIds?: IUnit["id"][]; // IDs of selected units (alternative to values)
    facilityId?: string; // Current facility ID
    unitOptions?: IUnit[] | IAppConfig["accessibleUnits"]; // Available units to choose from
    withQueryParams?: boolean; // Whether to sync selection with URL query params
    showFullUnitNames?: boolean; // Whether to show full unit names in selection display
    size?: SelectProps["size"]; // Size of the select component
    className?: string; // Additional CSS class name
  }) => {
    // State - for groups toggle (expanded/collapsed state)
    const [toggledGroups, setToggledGroups] = useState<Record<string, boolean> | null>(null);
    const clickedOnToggle = useRef(false); // Used to detect clicks on toggle vs group selection

    // Normalize valueIds from either values prop or valueIds prop
    const valueIds = useMemo(
      () => valueIdsFromParams || map(values, "id"),
      [values, valueIdsFromParams],
    );

    // Get available units from props or from app config
    const availableUnitsFromConfig = useAppConfigQuery().data?.accessibleUnits;
    const availableUnits: IUnit[] =
      (availableUnitsFromParams as IUnit[]) || availableUnitsFromConfig || emptyUnitArray;
    const facilityById = useFacilityById();

    // Create options array for the select component (filtering out archived units)
    const options = useMemo(
      () =>
        filter(availableUnits, (unit) => !unit.archivedAt).map((unit) => ({
          label: unit.name,
          value: unit.id,
        })),
      [availableUnits],
    );

    // Build groups structure - organize units by their group name
    const groups = useMemo(
      () =>
        availableUnits.reduce((acc, unit) => {
          // Determine group name: use unit group name, facility name, or NO_GROUP constant
          let groupName: string = unit.configuration.settings?.schedulerApp?.unitGroupName || "";
          groupName ||= facilityById[unit.facilityId]?.name || "";
          groupName ||= NO_GROUP;

          // Initialize group if it doesn't exist
          const group = (acc[groupName] ||= { available: [], selected: [] });
          group.available.push(unit.id);
          // Track which units in this group are selected
          if (valueIds.includes(unit.id)) group.selected.push(unit.id);

          return acc;
        }, {} as TGroups),
      [availableUnits, valueIds, facilityById],
    );

    // Auto-expand the first group that has a selected unit on initial render
    useEffect(() => {
      if (!!toggledGroups) return; // Skip if groups are already toggled

      const firstGroup = entries(groups).find(([_groupName, group]) => group.selected.length === 1);
      if (!firstGroup) return;

      setToggledGroups({ [firstGroup[0]]: true });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, !!toggledGroups]);

    // Helper lookups and derived state
    const optionsByValue = useKeyBy(options, "value"); // Quick lookup of options by value

    // Filter valueIds to only include available units
    const selectedValueIds = useMemo(
      () => intersection(valueIds, map(availableUnits, "id")),
      [availableUnits, valueIds],
    );

    // Identify which groups have all their units selected
    const fullySelectedGroups = useMemo(
      () =>
        entries(groups).reduce((acc, [groupName, group]) => {
          if (group.available.length === group.selected.length) acc.push(groupName);

          return acc;
        }, [] as string[]),
      [groups],
    );

    // Reverse mapping from unit ID to group name for quick lookups
    const reverseGroups = entries(groups).reduce(
      (acc, [groupName, group]) => {
        group.available.forEach((unitId) => {
          acc[unitId] = groupName;
        });

        return acc;
      },
      {} as Record<IUnit["id"], string>,
    );

    // Flag to determine if we should show group headers
    const hasMoreThanOneGroup = keys(groups).length > 1;

    // Handle URL query params synchronization
    useHandleQueryParams({
      onChange,
      valueIds,
      withQueryParams,
      fullySelectedGroups,
      groups,
      options,
      isLoading: false,
      availableUnits,
    });

    // Event handlers

    // Handle individual unit selection from the dropdown
    const onUnitSelect = useCallback(
      (event: SelectChangeEvent<unknown>) => {
        const newSelectedUnitIds = event.target.value as (typeof options)[number]["value"];

        let selectedUnits = availableUnits.filter((unit) => newSelectedUnitIds.includes(unit.id));

        // Special case: if "all" is selected or all units are selected, clear selection
        // we interpret an empty selection as "all"
        if (
          (newSelectedUnitIds.includes("all") && selectedValueIds.length) ||
          newSelectedUnitIds.length === availableUnits.length
        ) {
          selectedUnits = [];
        }

        onChange(selectedUnits);
      },
      [availableUnits, onChange, selectedValueIds],
    );

    // Handle selecting/deselecting an entire group
    const onGroupSelect = useCallback(
      (groupSelection: IUnit["id"][], allSelected: boolean) => {
        // If all units in group are selected, remove them; otherwise add them
        const updatedIdSelection = allSelected
          ? selectedValueIds.filter((unitId) => !groupSelection.includes(unitId))
          : uniq([...selectedValueIds, ...groupSelection]);

        let selectedUnits = availableUnits.filter((unit) => updatedIdSelection.includes(unit.id));

        // If all units are selected, clear selection (same behavior as onUnitSelect)
        if (updatedIdSelection.length === availableUnits.length) {
          selectedUnits = [];
        }

        onChange(selectedUnits);
      },
      [availableUnits, onChange, selectedValueIds],
    );

    // Component renderers

    // Render a group header with checkbox and toggle button
    const buildGroupElement = useCallback(
      (groupName: string, group: { available: IUnit["id"][]; selected: IUnit["id"][] }) => {
        if (!hasMoreThanOneGroup) return null; // Don't show group headers if only one group

        const allSelected = group.selected.length === group.available.length;
        const indeterminate = group.selected.length > 0 && !allSelected;
        const toggledClass = toggledGroups?.[groupName] ? "toggled" : "";

        return (
          <ListSubheader
            className="li-group-selector"
            onClickCapture={() => {
              // Set timeout to handle click after potential toggle button click
              // This prevents both toggling and selecting when clicking the group header
              setTimeout(() => {
                if (clickedOnToggle.current) return (clickedOnToggle.current = false);

                onGroupSelect(group.available, allSelected);
              });
            }}
          >
            <Checkbox
              indeterminate={indeterminate}
              checked={allSelected}
              trackingLabel="select-unit"
            />
            <ListItemText primary={groupName} />
            <ListItemButton
              className="toggle-group-button"
              onClick={() => {
                clickedOnToggle.current = true;
                setToggledGroups((prev) => ({
                  ...prev,
                  [groupName]: !prev?.[groupName],
                }));
              }}
            >
              <ArrowLeft className={`toggle-group-icon ${toggledClass}`} />
            </ListItemButton>
          </ListSubheader>
        );
      },
      [hasMoreThanOneGroup, onGroupSelect, toggledGroups],
    );

    // Render the selected values as chips in the select input
    const renderSelectedValues = useCallback(
      (selected: IUnit["id"][]) => {
        // If showFullUnitNames, show all units, else show groups
        let labels: string[] = [];

        if (showFullUnitNames) {
          // Show individual unit names
          labels = selected.map((value) => optionsByValue[value]?.label || "");
        } else {
          // Group by unit group and show counts
          const items = uniqBy(
            selected.map((value) => {
              const itemGroup = reverseGroups[value]!;

              return hasMoreThanOneGroup
                ? { group: itemGroup } // Show group name when multiple groups exist
                : fullySelectedGroups.includes(itemGroup)
                  ? { unit: "All" } // Show "All" when all units in a single group are selected
                  : { unit: optionsByValue[value]?.label || "" }; // Show unit name otherwise
            }),
            (item) => item.group || item.unit,
          );

          labels = items.map(({ group, unit }) =>
            group ? `(${groups[group]?.selected.length || 0}) ${group}` : unit || "",
          );
        }

        // Special case: if all groups are fully selected or "all" is selected, just show "All"
        if (fullySelectedGroups.length === keys(groups).length || selected.includes("all")) {
          labels = ["All"];
        }

        return (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
            }}
            className={`${className || ""}`}
          >
            {labels.map((label) => (
              <Chip key={label} label={label} size={size} sx={chipStyles} />
            ))}
          </Box>
        );
      },
      [
        size,
        fullySelectedGroups,
        groups,
        hasMoreThanOneGroup,
        optionsByValue,
        reverseGroups,
        showFullUnitNames,
        className,
      ],
    );

    // Render the complete component
    return (
      <FormControl
        size={size}
        className={`m7-unit-group-picker ${className || ""}`}
        sx={{ m: 1, width: 300 }}
      >
        <InputLabel id="unit-selector-label">Units</InputLabel>
        <Select
          size={size}
          labelId="unit-selector-label"
          id="unit-selector"
          MenuProps={{ className: "m7-unit-group-picker-menu deprecated" }}
          multiple
          value={!selectedValueIds.length ? ALL_SELECTED : selectedValueIds}
          onChange={onUnitSelect}
          input={<OutlinedInput label="Units" />}
          renderValue={renderSelectedValues}
        >
          {/* "All" option at the top */}
          <MenuItem trackingLabel={null} key={"all"} value={"all"}>
            <Checkbox checked={selectedValueIds.length === 0} trackingLabel="select-all-units" />
            <ListItemText primary={"All"} />
          </MenuItem>

          {/* Render each group with its units */}
          {entries(groups).map(([groupName, group]) => {
            const groupElement = buildGroupElement(groupName, group);

            return [
              groupElement,
              // Map over all options, but only show the ones that are part of the group
              options.map(({ value, label }) => {
                if (!group.available.includes(value)) return null;

                const hidden = hasMoreThanOneGroup && !toggledGroups?.[groupName];

                return (
                  <MenuItem
                    trackingLabel={null}
                    key={value}
                    value={value}
                    sx={{
                      ...(hasMoreThanOneGroup ? { pl: 4 } : {}), // Indent if in a group
                      ...(hidden ? { display: "none" } : {}), // Hide if group is collapsed
                    }}
                  >
                    <Checkbox
                      checked={selectedValueIds.indexOf(value) > -1}
                      trackingLabel="select-unit-group"
                    />
                    <ListItemText className={"m7-item-name"} primary={label} />
                  </MenuItem>
                );
              }),
            ];
          })}
        </Select>
      </FormControl>
    );
  },
);
